import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useCompanyData } from '@vibesites/runtime/react';
import {
  COMMUNITY_BASE_PATH,
  getAvailableCities,
  getAvailableStates,
  getCommunityPath,
  getCommunityProperties,
  getStateName,
  parseCommunitySearchQuery,
  propertyMatchesParsedSearch,
  type CommunityProperty,
} from './propertyUtils';

type SuggestionType = 'state' | 'city' | 'community' | 'search';

interface Suggestion {
  label: string;
  type: SuggestionType;
  url: string;
  meta?: string;
}

interface CommunitySearchProps {
  placeholder?: string;
  /** Cycles as a typewriter placeholder when the input is empty (hero search). */
  typingPlaceholders?: string[];
  buttonText?: ReactNode;
  /** `hero` uses a solid white search bar for dark full-bleed heroes */
  variant?: 'default' | 'hero';
  /** When set, state/city/search results filter the current page instead of navigating to the communities page. */
  onApplySearchParams?: (params: URLSearchParams) => void;
}

const TYPE_LABELS: Record<SuggestionType, string> = {
  state: 'States',
  city: 'Cities',
  community: 'Communities',
  search: 'Search',
};

const TYPE_ORDER: SuggestionType[] = ['search', 'state', 'city', 'community'];

function buildSuggestions(properties: CommunityProperty[]): Suggestion[] {
  const suggestions: Suggestion[] = [];
  const seenStates = new Set<string>();
  const seenCities = new Set<string>();

  for (const prop of properties) {
    const stateSlug = prop._stateSlug;
    const citySlug = prop._citySlug;
    if (!stateSlug || !citySlug) continue;

    if (!seenStates.has(stateSlug)) {
      seenStates.add(stateSlug);
      suggestions.push({
        label: getStateName((prop.property_address_state || stateSlug).toUpperCase()),
        type: 'state',
        url: `${COMMUNITY_BASE_PATH}?state=${encodeURIComponent(stateSlug)}#communities-results`,
      });
    }

    const cityKey = `${stateSlug}/${citySlug}`;
    if (!seenCities.has(cityKey)) {
      seenCities.add(cityKey);
      const city = prop.property_address_city || citySlug;
      const abbr = (prop.property_address_state || stateSlug).toUpperCase();
      suggestions.push({
        label: `${city}, ${abbr}`,
        type: 'city',
        url: `${COMMUNITY_BASE_PATH}?state=${encodeURIComponent(stateSlug)}&city=${encodeURIComponent(citySlug)}#communities-results`,
      });
    }

    if (prop.property_name) {
      suggestions.push({
        label: prop.property_name,
        type: 'community',
        url: getCommunityPath(prop),
        meta: [prop.property_address_city, (prop.property_address_state || '').toUpperCase()]
          .filter(Boolean)
          .join(', '),
      });
    }
  }

  return suggestions;
}

function highlightMatch(text: string, query: string) {
  if (!query) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <strong>{text.slice(idx, idx + query.length)}</strong>
      {text.slice(idx + query.length)}
    </>
  );
}

function navigateTo(url: string) {
  const nav = (window as unknown as { __vibesites_navigate?: (to: string) => void }).__vibesites_navigate;
  if (nav) {
    nav(url);
    return;
  }
  const anchor = document.createElement('a');
  anchor.href = url;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

function useTypingPlaceholder(phrases: string[] | undefined, enabled: boolean) {
  const [display, setDisplay] = useState(phrases?.[0] ?? '');
  const phraseIndex = useRef(0);
  const charIndex = useRef(0);
  const deleting = useRef(false);

  useEffect(() => {
    if (!enabled || !phrases || phrases.length === 0) return;

    phraseIndex.current = 0;
    charIndex.current = 0;
    deleting.current = false;
    setDisplay('');

    let timeoutId: ReturnType<typeof setTimeout>;

    const tick = () => {
      const phrase = phrases[phraseIndex.current] ?? '';
      if (!deleting.current) {
        charIndex.current += 1;
        setDisplay(phrase.slice(0, charIndex.current));
        if (charIndex.current >= phrase.length) {
          deleting.current = true;
          timeoutId = setTimeout(tick, 1800);
          return;
        }
        timeoutId = setTimeout(tick, 55);
        return;
      }

      charIndex.current -= 1;
      setDisplay(phrase.slice(0, Math.max(0, charIndex.current)));
      if (charIndex.current <= 0) {
        deleting.current = false;
        phraseIndex.current = (phraseIndex.current + 1) % phrases.length;
        timeoutId = setTimeout(tick, 400);
        return;
      }
      timeoutId = setTimeout(tick, 28);
    };

    timeoutId = setTimeout(tick, 400);
    return () => clearTimeout(timeoutId);
  }, [enabled, phrases]);

  return display;
}

function looksLikeNaturalLanguage(query: string) {
  const q = query.trim();
  if (q.length < 8) return false;
  if (/\s/.test(q)) return true;
  return /pet|bedroom|bath|pool|fitness|under|\$\d/i.test(q);
}

export function CommunitySearch({
  placeholder = 'Try “pet friendly apartments in tampa”...',
  typingPlaceholders,
  buttonText = 'Find a home',
  variant = 'default',
  onApplySearchParams,
}: CommunitySearchProps) {
  const isHero = variant === 'hero';
  const companyData = useCompanyData();
  const properties = useMemo(() => getCommunityProperties(companyData), [companyData]);
  const allSuggestions = useMemo(() => buildSuggestions(properties), [properties]);
  const availableCities = useMemo(() => getAvailableCities(properties), [properties]);
  const availableStates = useMemo(() => getAvailableStates(properties), [properties]);

  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const typingEnabled = Boolean(isHero && typingPlaceholders?.length && !query && !isFocused);
  const typedPlaceholder = useTypingPlaceholder(typingPlaceholders, typingEnabled);
  const inputPlaceholder = typingEnabled ? undefined : placeholder;

  const filtered = useMemo(() => {
    const trimmed = query.trim();
    if (!trimmed) return [];

    const escaped = trimmed.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(escaped, 'i');
    const labelMatches = allSuggestions.filter((item) => regex.test(item.label));

    const seen = new Set<string>();
    const deduped: Suggestion[] = [];
    for (const match of labelMatches) {
      const key = `${match.type}:${match.label}`;
      if (seen.has(key)) continue;
      seen.add(key);
      deduped.push(match);
    }

    const grouped: Suggestion[] = [];

    if (looksLikeNaturalLanguage(trimmed)) {
      const parsed = parseCommunitySearchQuery(trimmed, availableCities, availableStates);
      const nlMatches = properties.filter((prop) => propertyMatchesParsedSearch(prop, parsed)).slice(0, 8);
      if (nlMatches.length > 0) {
        grouped.push({
          label: `Search “${trimmed}”`,
          type: 'search',
          url: `${COMMUNITY_BASE_PATH}?search=${encodeURIComponent(trimmed)}#communities-results`,
          meta: `${nlMatches.length} match${nlMatches.length === 1 ? '' : 'es'}`,
        });
        for (const prop of nlMatches) {
          const key = `community:${prop.property_name}`;
          if (seen.has(key)) continue;
          seen.add(key);
          grouped.push({
            label: prop.property_name || 'Community',
            type: 'community',
            url: getCommunityPath(prop),
            meta: [prop.property_address_city, (prop.property_address_state || '').toUpperCase()]
              .filter(Boolean)
              .join(', '),
          });
        }
      }
    }

    for (const type of TYPE_ORDER) {
      if (type === 'search') continue;
      grouped.push(...deduped.filter((item) => item.type === type));
    }

    // Prefer unique urls while preserving order.
    const urls = new Set<string>();
    const unique: Suggestion[] = [];
    for (const item of grouped) {
      if (urls.has(item.url)) continue;
      urls.add(item.url);
      unique.push(item);
    }
    return unique.slice(0, 14);
  }, [query, allSuggestions, availableCities, availableStates, properties]);

  const go = useCallback((url: string) => {
    setIsOpen(false);
    setQuery('');
    const target = new URL(url, window.location.origin);
    if (onApplySearchParams && target.pathname === COMMUNITY_BASE_PATH) {
      onApplySearchParams(target.searchParams);
      return;
    }
    navigateTo(url);
  }, [onApplySearchParams]);

  const handleSubmit = useCallback((event: FormEvent) => {
    event.preventDefault();
    if (activeIndex >= 0 && activeIndex < filtered.length) {
      go(filtered[activeIndex].url);
      return;
    }
    const q = query.trim();
    go(
      q
        ? `${COMMUNITY_BASE_PATH}?search=${encodeURIComponent(q)}#communities-results`
        : `${COMMUNITY_BASE_PATH}#communities-results`,
    );
  }, [activeIndex, filtered, query, go]);

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    listRef.current.querySelectorAll('[role="option"]')[activeIndex]?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const groupedForRender = useMemo(() => {
    const groups: { type: SuggestionType; items: { suggestion: Suggestion; globalIndex: number }[] }[] = [];
    let currentType: SuggestionType | null = null;
    filtered.forEach((suggestion, index) => {
      if (suggestion.type !== currentType) {
        currentType = suggestion.type;
        groups.push({ type: suggestion.type, items: [] });
      }
      groups[groups.length - 1].items.push({ suggestion, globalIndex: index });
    });
    return groups;
  }, [filtered]);

  const showDropdown = isOpen && filtered.length > 0;

  return (
    <div ref={containerRef} className={`relative w-full ${isHero ? 'max-w-2xl' : 'max-w-none'}`}>
      <form
        className={
          isHero
            ? 'flex h-[70px] w-full items-center gap-3 rounded-2xl border border-black/5 bg-white pl-4 pr-2 shadow-md transition-all focus-within:border-black/10 focus-within:shadow-lg'
            : 'flex w-full overflow-hidden rounded-md bg-[rgb(var(--ala-background))] shadow-lg'
        }
        onSubmit={handleSubmit}
        autoComplete="off"
      >
        {isHero ? (
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[rgb(var(--ala-text))]/5" aria-hidden>
            <svg viewBox="0 0 24 24" className="h-[18px] w-[18px] text-[rgb(var(--ala-accent))]" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="7" />
              <path d="M20 20l-3-3" />
            </svg>
          </span>
        ) : null}
        <div className="relative min-w-0 flex-1">
          {typingEnabled ? (
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 flex items-center overflow-hidden font-body text-[15px] text-[rgb(var(--ala-text-muted))] md:text-base"
            >
              <span className="truncate">{typedPlaceholder}</span>
              <span className="search-typing-caret ml-px inline-block h-[1.15em] w-[2px] shrink-0 self-center rounded-[1px] bg-[rgb(var(--ala-accent))]" />
            </div>
          ) : null}
          <input
            type="search"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setActiveIndex(-1);
              setIsOpen(event.target.value.trim().length > 0);
            }}
            onKeyDown={(event) => {
              if (!showDropdown) return;
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                setActiveIndex((prev) => (prev + 1) % filtered.length);
              } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                setActiveIndex((prev) => (prev <= 0 ? filtered.length - 1 : prev - 1));
              } else if (event.key === 'Escape') {
                setIsOpen(false);
                setActiveIndex(-1);
              }
            }}
            onFocus={() => {
              setIsFocused(true);
              if (query.trim() && filtered.length > 0) setIsOpen(true);
            }}
            onBlur={() => setIsFocused(false)}
            placeholder={inputPlaceholder}
            aria-label={placeholder}
            aria-expanded={showDropdown}
            aria-autocomplete="list"
            aria-controls="community-search-listbox"
            role="combobox"
            className={
              isHero
                ? 'relative z-[1] w-full bg-transparent font-body text-[15px] text-[rgb(var(--ala-text))] outline-none placeholder:text-[rgb(var(--ala-text-muted))] md:text-base'
                : 'w-full bg-transparent px-4 py-3 font-body text-base text-[rgb(var(--ala-text))] outline-none'
            }
          />
        </div>
        <button
          type="submit"
          className={
            isHero
              ? 'inline-flex h-11 min-w-11 shrink-0 items-center justify-center rounded-full bg-[rgb(var(--ala-accent))] px-4 text-[rgb(var(--ala-on-primary))] transition hover:bg-[#b51f1f]'
              : 'bg-[rgb(var(--ala-primary))] px-5 py-3 font-heading text-sm text-[rgb(var(--ala-on-primary))] transition hover:bg-[rgb(var(--ala-primary))]/90'
          }
          aria-label={typeof buttonText === 'string' ? buttonText : 'Search'}
        >
          {isHero ? (
            <>
              {buttonText}
              <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <path d="M5 12h14M13 5l7 7-7 7" />
              </svg>
            </>
          ) : (
            buttonText
          )}
        </button>
      </form>

      {showDropdown && (
        <div
          ref={listRef}
          id="community-search-listbox"
          role="listbox"
          className="absolute z-20 mt-2 max-h-80 w-full overflow-auto rounded-md bg-[rgb(var(--ala-background))] text-[rgb(var(--ala-text))] shadow-xl"
        >
          {groupedForRender.map((group) => (
            <div key={group.type}>
              <div className="px-4 py-2 font-heading text-xs uppercase tracking-wide text-[rgb(var(--ala-text-muted))]">
                {TYPE_LABELS[group.type]}
              </div>
              {group.items.map(({ suggestion, globalIndex }) => (
                <div
                  key={`${suggestion.type}:${suggestion.url}`}
                  role="option"
                  aria-selected={globalIndex === activeIndex}
                  className={`cursor-pointer px-4 py-2 font-body text-sm ${globalIndex === activeIndex ? 'bg-[rgb(var(--ala-surface))]' : ''}`}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    go(suggestion.url);
                  }}
                  onMouseEnter={() => setActiveIndex(globalIndex)}
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <span>{highlightMatch(suggestion.label, query.trim())}</span>
                    {suggestion.meta ? (
                      <span className="shrink-0 font-body text-xs text-[rgb(var(--ala-text-muted))]">{suggestion.meta}</span>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default CommunitySearch;
